import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { TextField } from '@/components/ui/TextField';
import { useSessionStore } from '@/store/useSessionStore';

/** Read domain + token + session info from the current URL (supports the pickrr param names). */
function readConnectParams(): { domain: string; token: string; sessionInfo: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    domain: (params.get('merchant') || params.get('merchantDomain') || params.get('shop') || '').trim(),
    token: (params.get('token') || params.get('x-auth-token') || params.get('authToken') || '').trim(),
    sessionInfo: (params.get('sessionInfo') || params.get('x-session-info') || '').trim(),
  };
}

/**
 * Shown when the dashboard has no merchant/token (standalone use, or after the
 * session expired). In production the host injects these via ?merchant= /
 * ?token=, so this is mainly a dev / fallback affordance.
 */
export function ConnectScreen() {
  const merchantDomain = useSessionStore((state) => state.merchantDomain);
  const authExpired = useSessionStore((state) => state.authExpired);
  const connect = useSessionStore((state) => state.connect);

  const urlParams = readConnectParams();
  const [domainInput, setDomainInput] = useState(urlParams.domain || merchantDomain);
  const [tokenInput, setTokenInput] = useState(urlParams.token);

  // Auto-connect on load when both values are present in the URL.
  useEffect(() => {
    if (urlParams.domain && urlParams.token) {
      connect(urlParams.domain, urlParams.token, urlParams.sessionInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!domainInput.trim() || !tokenInput.trim()) {
      return;
    }
    connect(domainInput, tokenInput);
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-surface-muted px-4">
      <Card className="w-full max-w-md">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-sm font-bold text-white">
            RP
          </span>
          <span className="text-base font-bold tracking-tight text-ink">Fastrr Return — Merchant</span>
        </div>

        {authExpired ? (
          <p className="mb-4 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning">
            Your session expired. Reconnect with a fresh dashboard token.
          </p>
        ) : (
          <p className="mb-4 text-sm text-ink-muted">
            Connect with your store domain and Fastrr dashboard session token.
          </p>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <TextField
            label="Store domain"
            placeholder="brand.myshopify.com"
            value={domainInput}
            onChange={(event) => setDomainInput(event.target.value)}
            autoFocus
          />
          <TextField
            label="Dashboard auth token"
            placeholder="x-auth-token"
            value={tokenInput}
            onChange={(event) => setTokenInput(event.target.value)}
          />
          <Button type="submit" disabled={!domainInput.trim() || !tokenInput.trim()}>
            Connect
          </Button>
        </form>
      </Card>
    </div>
  );
}
